/**
 * Advertising Optimization Arche Page — route wrapper.
 *
 * Mounted at /a/arche_advertising_optimization/*. Holder-scoped via the
 * shared <HolderSwitcher>; empty-state until a workspace is picked
 * (fias-leads pattern).
 *
 * Sub-routes:
 *   /                       → DashboardPage
 *   /campaigns              → CampaignsListPage
 *   /campaigns/new          → CampaignWizardPage
 *   /campaigns/:campaignId  → CampaignDetailPage
 *   /experiments            → ExperimentsPage
 *   /experiments/:experimentId → ExperimentDetailPage
 *   /links                  → LinksPage
 *   /settings               → SettingsPage
 */

import { Routes, Route } from 'react-router-dom';
import { ArcheLayout } from '../../../components/ArcheLayout';
import { ArcheHeader } from '../../../components/ArcheHeader';
import { HolderSwitcher } from '@/components/HolderSwitcher';
import { useArcheAdmin } from '@/auth/hooks/useArcheAdmin';
import { ARCHE_ID } from '../constants';
import { useSelectedHolder } from '../hooks/useSelectedHolder';
import { AdoptNav } from '../components/AdoptNav';
import { DashboardPage } from './DashboardPage';
import { CampaignsListPage } from './CampaignsListPage';
import { CampaignWizardPage } from './CampaignWizardPage';
import { CampaignDetailPage } from './CampaignDetailPage';
import { ExperimentsPage } from './ExperimentsPage';
import { ExperimentDetailPage } from './ExperimentDetailPage';
import { ActionInboxPage } from './ActionInboxPage';
import { LinksPage } from './LinksPage';
import { SettingsPage } from './SettingsPage';

function NoHolderEmptyState({
  holderId,
  setHolderId,
}: {
  holderId: string | null;
  setHolderId: (id: string | null) => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 p-12 text-center">
      <h2 className="text-xl font-semibold text-foreground">Choose a workspace to get started</h2>
      <p className="max-w-md text-muted-foreground">
        Advertising Optimization runs ad campaigns for a workspace — its campaigns, budgets, and
        attribution data are scoped to the Holder you pick.
      </p>
      <HolderSwitcher selectedHolderId={holderId} onChange={(id) => setHolderId(id)} />
    </div>
  );
}

function AdvertisingOptimizationArcheContent() {
  const { holderId, setHolderId } = useSelectedHolder();

  return (
    <ArcheLayout>
      <div className="flex flex-col h-full">
        <ArcheHeader archeId={ARCHE_ID} title="AdOpt - Advertising Optimization">
          <HolderSwitcher selectedHolderId={holderId} onChange={(id) => setHolderId(id)} />
        </ArcheHeader>
        {/* Focus-mode restore is handled centrally by ArcheLayout's expanded
            header (Back-to-Fias + Fias AI + Collapse) — no per-arche control. */}
        {holderId && <AdoptNav />}
        <div className="flex-1 overflow-auto">
          {!holderId ? (
            <NoHolderEmptyState holderId={holderId} setHolderId={setHolderId} />
          ) : (
            <Routes>
              <Route path="/" element={<DashboardPage holderId={holderId} />} />
              <Route path="/campaigns" element={<CampaignsListPage holderId={holderId} />} />
              <Route path="/campaigns/new" element={<CampaignWizardPage holderId={holderId} />} />
              <Route
                path="/campaigns/:campaignId"
                element={<CampaignDetailPage holderId={holderId} />}
              />
              <Route path="/experiments" element={<ExperimentsPage holderId={holderId} />} />
              <Route
                path="/experiments/:experimentId"
                element={<ExperimentDetailPage holderId={holderId} />}
              />
              <Route path="/actions" element={<ActionInboxPage holderId={holderId} />} />
              <Route path="/links" element={<LinksPage holderId={holderId} />} />
              <Route path="/settings" element={<SettingsPage holderId={holderId} />} />
            </Routes>
          )}
        </div>
      </div>
    </ArcheLayout>
  );
}

/**
 * Admin-only notice. Matches the server gate in
 * routes/arches/advertising-optimization/index.ts — without this the page would
 * still mount for a non-admin and every API call underneath would 403, which
 * reads as "the arche is broken" rather than "not available to you".
 */
function AdminOnlyNotice() {
  return (
    <ArcheLayout>
      <div className="flex flex-col h-full">
        <ArcheHeader archeId={ARCHE_ID} title="AdOpt - Advertising Optimization" />
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-12 text-center">
          <h2 className="text-xl font-semibold text-foreground">Not available yet</h2>
          <p className="max-w-md text-muted-foreground">
            Advertising Optimization is in internal testing and is currently limited to platform
            administrators.
          </p>
        </div>
      </div>
    </ArcheLayout>
  );
}

export function AdvertisingOptimizationArchePage() {
  // Platform `admin` only — no domain-specific admin role for this arche, so
  // `useArcheAdmin()` with no args (mirrors the server's `requireAdmin`).
  // This is a UX gate, NOT the security boundary: the server gate is.
  const isAdmin = useArcheAdmin();
  if (!isAdmin) return <AdminOnlyNotice />;
  return <AdvertisingOptimizationArcheContent />;
}
